import React, { useEffect, useState } from 'react';
import azureDevOpsService from './services/azureDevOpsService';
import { isSkippedStatus } from './utils/testStatus';

function isFlaky(testHistory) {
  const last3 = testHistory.slice(0, 3);
  if (last3.length < 3) return false;
  if (last3.every(e => e.status === 'failed')) return false;
  if (last3.every(e => e.status === 'passed')) return false;
  const last10 = testHistory.slice(0, 10);
  const failedCount = last10.filter(e => e.status === 'failed').length;
  return failedCount >= 4;
}

export default function KpiFlakyTestsUnified() {
  const PIPELINES = [
    { id: 601, name: 'RF Admin API' },
    { id: 602, name: 'RF Admin Browser' },
    { id: 603, name: 'RF Config API' },
    { id: 604, name: 'RF Config Browser' },
    { id: 605, name: 'RF DE API' },
    { id: 606, name: 'RF DE Browser' },
    { id: 607, name: 'RF Pelias' },
    { id: 608, name: 'RF DP API' },
    { id: 609, name: 'RF DP Browser' },
    { id: 610, name: 'RF Investigation API' },
    { id: 611, name: 'RF Agentique API' },
    { id: 612, name: 'RF Agentique Browser' },
    { id: 613, name: 'RF Assistant API' },
    { id: 614, name: 'RF Assistant Browser' },
    { id: 615, name: 'RF OSINT API' },
    { id: 616, name: 'RF OSINT Browser' },
    { id: 303, name: 'ReadyAPI - Admin Tests' },
    { id: 304, name: 'ReadyAPI - Data Preparation' },
    { id: 305, name: 'ReadyAPI - Data Exploration' },
    { id: 355, name: 'ReadyAPI - Investigation' }
  ];

  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [flakyTests, setFlakyTests] = useState([]);
  const [allTests, setAllTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const analyzePipeline = async (pipelineId) => {
    setLoading(true);
    setError(null);
    setFlakyTests([]);
    setAllTests([]);

    try {
      // 1) Récupérer les derniers builds du pipeline (augmenter pour couvrir 10 exécutions)
      const BUILDS_TO_SCAN = 12; // généralement suffit pour couvrir 10 exécutions/test
      const buildsRes = await azureDevOpsService.makeApiCall(
        `/build/builds?definitions=${pipelineId}&$top=${BUILDS_TO_SCAN}&queryOrder=finishTimeDescending&api-version=7.0`
      );
      const builds = (buildsRes.value || []).sort((a,b) => new Date(b.finishTime || b.queueTime || 0) - new Date(a.finishTime || a.queueTime || 0));

      // 2) Agréger tous les résultats une seule fois par build
      const resultsByTest = new Map(); // testName -> [{status, dateTimestamp}]
      const trackedNames = new Set(); // noms à suivre (issus du build le plus récent)

      for (let idx = 0; idx < builds.length; idx++) {
        const build = builds[idx];
        const results = await azureDevOpsService.getTestResultsForBuild(build.id);

        if (idx === 0) {
          // Premier build (le plus récent): tous les tests deviennent "tracked"
          for (const r of (results || [])) {
            if (r.automatedTestName) trackedNames.add(r.automatedTestName);
          }
        }

        // Pour tous les builds, on collecte les résultats des tests trackés
        for (const r of (results || [])) {
          const testName = r.automatedTestName;
          if (!testName || !trackedNames.has(testName)) continue;

          // Un test non exécuté (SKIP) n'est ni un succès ni un échec : l'inclure
          // ferait passer pour "flaky" un test simplement désactivé sur certains runs.
          if (isSkippedStatus(r.outcome)) continue;
          const status = r.outcome === 'Passed' ? 'passed' : 'failed';
          const dateTimestamp = new Date(r.completedDate || r.startedDate || 0).getTime();

          if (!resultsByTest.has(testName)) {
            resultsByTest.set(testName, []);
          }
          resultsByTest.get(testName).push({ status, dateTimestamp });
        }
      }

      // 3) Trier chaque historique par date DESC et vérifier la "flakyness"
      const flaky = [];
      for (const [name, hist] of resultsByTest.entries()) {
        hist.sort((a,b) => b.dateTimestamp - a.dateTimestamp);
        
        if (hist.length === 0) continue;
        if (isFlaky(hist)) {
          flaky.push({ name, pipelineId });
        }
      }

      setAllTests(Array.from(trackedNames));
      setFlakyTests(flaky);
    } catch (e) {
      setError(e?.message || 'Erreur lors de l\'analyse du pipeline');
    } finally {
      setLoading(false);
    }
  };

  const handlePipelineSelect = (e) => {
    const value = e.target.value;
    if (!value) {
      setSelectedPipeline(null);
      setFlakyTests([]);
      setAllTests([]);
      setError(null);
      return;
    }
    const pipelineId = parseInt(value);
    setSelectedPipeline(pipelineId);
    analyzePipeline(pipelineId);
  };

  const selectedPipelineInfo = PIPELINES.find(p => p.id === selectedPipeline);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-bold mb-4">Tests Flaky - Tous Pipelines</h2>

      {/* Pipeline Selector */}
      <div className="mb-6">
        <label htmlFor="pipeline-select" className="block text-sm font-medium text-gray-700 mb-2">
          Sélectionner un pipeline
        </label>
        <select
          id="pipeline-select"
          value={selectedPipeline || ''}
          onChange={handlePipelineSelect}
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">-- Choisir un pipeline --</option>
          {PIPELINES.map(p => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="py-12 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Analyse en cours...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {/* Results */}
      {!loading && selectedPipeline && !error && (
        <div>
          {selectedPipelineInfo && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <span className="text-sm font-medium text-gray-700">Pipeline sélectionné: </span>
              <span className="text-sm text-gray-900">{selectedPipelineInfo.name}</span>
              <span className="ml-2 text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                {selectedPipelineInfo.type}
              </span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm text-gray-600">Tests analysés</div>
              <div className="text-2xl font-bold text-blue-600">{allTests.length}</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <div className="text-sm text-gray-600">Tests flaky détectés</div>
              <div className="text-2xl font-bold text-red-600">{flakyTests.length}</div>
            </div>
          </div>

          {flakyTests.length > 0 ? (
            <div className="space-y-2">
              {flakyTests.map((test, idx) => (
                <div key={idx} className="border border-red-200 rounded-lg p-3 bg-red-50">
                  <div className="font-medium text-gray-900">{test.name}</div>
                  <div className="text-sm text-gray-600 mt-1">
                    Instabilité détectée dans les 10 dernières exécutions
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-green-600 font-medium">
              ✓ Aucun test flaky détecté
            </div>
          )}
        </div>
      )}

      {/* Initial State */}
      {!loading && !selectedPipeline && (
        <div className="text-center py-6 text-gray-600">
          Choisissez un pipeline pour commencer l'analyse.
        </div>
      )}
    </div>
  );
}
